import { Link } from "react-router-dom";
import { Icons, getIcon } from "@/components/Icons";
import { arrowFocus } from "./keys";
import type { Area, BadgeKey, Item } from "./model";

export function PageList({ area, activeItemId, badges, collapsed, open, onToggle, onNavigate }: {
  area: Area;
  activeItemId: string | null;
  badges: Partial<Record<BadgeKey, number>>;
  collapsed: boolean;
  /** Tablet: slid over the content. Ignored on desktop. */
  open: boolean;
  onToggle: () => void;
  onNavigate: () => void;
}) {
  return (
    <aside
      className="sidebar page-list"
      data-open={open ? "true" : "false"}
      aria-label={`${area.label} pages`}
      onKeyDown={(e) => arrowFocus(e, ".nav-link")}
    >
      <div className="sidebar-brand"><div className="label"><div>{area.label}</div></div></div>
      {area.groups.map((g, gi) => (
        <div key={g.label ?? gi} role="group" aria-label={g.label}>
          {g.label && <div className="sidebar-section">{g.label}</div>}
          {g.items.map((it) => (
            <PageLink key={it.id} item={it} active={it.id === activeItemId}
              badge={it.badge ? badges[it.badge] : undefined} onNavigate={onNavigate} />
          ))}
        </div>
      ))}
      <div className="sidebar-footer">
        <button type="button" className="nav-link collapse-toggle" onClick={onToggle} aria-label={collapsed ? "Expand menu" : "Collapse menu"}>
          {collapsed ? <Icons.ChevRight size={18} className="icon" /> : <Icons.ChevLeft size={18} className="icon" />}
          <span className="label">Collapse</span>
        </button>
      </div>
    </aside>
  );
}

function PageLink({ item, active, badge, onNavigate }: { item: Item; active: boolean; badge?: number; onNavigate: () => void }) {
  const I = getIcon(item.icon);
  const body = (
    <>
      <I size={18} className="icon" />
      <span className="label">{item.label}</span>
      {badge ? <span className="badge">{badge}</span> : null}
    </>
  );
  if (item.external) {
    return <a href={item.path} target="_blank" rel="noreferrer" className="nav-link" title={item.label}>{body}</a>;
  }
  return (
    <Link to={item.path} className={"nav-link" + (active ? " active" : "")} aria-current={active ? "page" : undefined}
      title={item.label} onClick={onNavigate}>
      {body}
    </Link>
  );
}
